import { useRef, useState } from "react";
import { FileText, X, Loader2, Download, Paperclip, ClipboardCopy, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { uploadDocument, fileUrl } from "@/lib/api";
import AttachmentPreview from "@/components/AttachmentPreview";

/**
 * Drop-or-click attachments for non-image files (PDF, Word, text, zip, etc).
 *
 * `attachments` is an array of `{path, filename, content_type, size}` objects
 * so we can show pretty filenames + badges. We store the full record (not just
 * a URL) because object storage paths are opaque and we want "Report.pdf"
 * on the chip, not "keepflow/docs/abc-123.pdf".
 */
export default function DocumentAttacher({ attachments = [], onChange, compact = false, triggerRef = null }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const inputRef = useRef(null);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const next = [...attachments];
    for (const f of files) {
      try {
        const data = await uploadDocument(f);
        const item = { path: data.path, filename: data.filename || f.name, size: data.size, content_type: data.content_type };
        next.push(item);
      } catch (err) {
        const code = err?.response?.status;
        const detail = err?.response?.data?.detail || err.message;
        toast.error(
          code === 413 ? "File too large (max 20 MB)"
          : code === 415 ? "That file type isn't supported"
          : `Upload failed: ${detail}`
        );
      }
    }
    onChange(next);
    setUploading(false);
  };

  const remove = (path) => {
    onChange(attachments.filter((a) => a.path !== path));
  };

  const prettySize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-2">
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { if (!compact) { e.preventDefault(); setDragging(true); } }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        className={cn(
          "cursor-pointer rounded-xl border-2 border-dashed px-3 py-3 text-sm transition-colors text-center",
          dragging
            ? "border-amber-500 bg-amber-50/50 dark:bg-amber-950/30"
            : "border-stone-300 dark:border-stone-700 hover:border-stone-500 text-stone-500 dark:text-stone-400",
          // `hidden` LAST so tailwind-merge keeps it over other utilities.
          compact && "hidden"
        )}
        data-testid="doc-dropzone"
      >
        {uploading ? (
          <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</span>
        ) : (
          <span className="inline-flex items-center gap-2"><Paperclip className="h-4 w-4" /> Drop a document, or click to pick (PDF, Word, text — up to 20 MB)</span>
        )}
      </div>

      {attachments.length > 0 && (
        <ul className="space-y-1.5">
          {attachments.map((a) => {
            const isAudio = (a.content_type || "").startsWith("audio/");
            return (
            <li
              key={a.path}
              className="flex flex-col gap-1.5 rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800/50 px-2.5 py-2 text-sm"
              data-testid={`doc-row-${a.filename}`}
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-stone-500" />
                <button
                  type="button"
                  onClick={() => setPreviewing(a)}
                  className="flex-1 min-w-0 text-left group"
                  data-testid={`doc-open-preview-${a.filename}`}
                >
                  <div className="truncate font-medium group-hover:text-amber-600 dark:group-hover:text-amber-400">{a.filename}</div>
                  <div className="text-xs text-stone-500">{prettySize(a.size)}</div>
                </button>
                <a
                  href={fileUrl(a.path)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-full text-stone-500 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-200 dark:hover:bg-stone-700"
                  data-testid={`doc-download-${a.filename}`}
                  aria-label="Download"
                >
                  <Download className="h-4 w-4" />
                </a>
                <button
                  type="button"
                  onClick={() => remove(a.path)}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-full text-stone-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                  data-testid={`doc-remove-${a.filename}`}
                  aria-label="Remove"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {/* Inline player + Whisper transcript for audio attachments.
                  Lets the user listen without opening the preview dialog and
                  copy the transcript with one click. */}
              {isAudio && (
                <audio controls src={fileUrl(a.path)} className="w-full h-9" data-testid={`doc-audio-${a.filename}`} />
              )}
              {isAudio && a.transcript !== undefined && (
                <TranscriptBlock
                  attachment={a}
                  onChange={(text) => {
                    const next = attachments.map((x) => x.path === a.path ? { ...x, transcript: text } : x);
                    onChange(next);
                  }}
                />
              )}
            </li>
            );
          })}
        </ul>
      )}

      <input
        ref={(el) => { inputRef.current = el; if (triggerRef) triggerRef.current = el; }}
        type="file"
        multiple
        hidden
        accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.csv,.json,.xml,.md,.ppt,.pptx,.xls,.xlsx,.zip,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        data-testid="doc-file-input"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
      />
      <AttachmentPreview attachment={previewing} onClose={() => setPreviewing(null)} />
    </div>
  );
}


/**
 * Inline-editable Whisper transcript. Tap the quote (or the pencil) to flip
 * into a textarea; blur / Enter / tap-away commits. Edits are propagated
 * through `onChange(text)` so the parent persists them with the note.
 */
function TranscriptBlock({ attachment, onChange }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(attachment.transcript || "");

  const commit = () => {
    setEditing(false);
    const next = text.trim();
    if (next !== (attachment.transcript || "").trim()) onChange(next);
  };

  return (
    <div className="flex items-start gap-1.5 text-xs text-stone-700 dark:text-stone-200 bg-white dark:bg-stone-900 rounded-md p-2 border border-stone-200 dark:border-stone-700">
      {editing ? (
        <textarea
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); }
            if (e.key === "Escape") { setText(attachment.transcript || ""); setEditing(false); }
          }}
          rows={Math.min(6, Math.max(2, text.split("\n").length))}
          placeholder="Add a transcript…"
          className="flex-1 bg-transparent outline-none resize-none text-xs leading-relaxed italic"
          data-testid={`doc-transcript-input-${attachment.filename}`}
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="flex-1 text-left italic leading-relaxed cursor-text"
          data-testid={`doc-transcript-view-${attachment.filename}`}
          title="Tap to edit"
        >
          {text ? `"${text}"` : <span className="text-stone-400">No transcript yet — tap to add one</span>}
        </button>
      )}
      {!editing && text && (
        <>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="h-6 w-6 inline-flex items-center justify-center rounded-full text-stone-500 hover:bg-stone-200 dark:hover:bg-stone-700 shrink-0"
            data-testid={`doc-transcript-edit-${attachment.filename}`}
            aria-label="Edit transcript"
            title="Edit transcript"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(text);
              toast.success("Transcript copied");
            }}
            className="h-6 w-6 inline-flex items-center justify-center rounded-full text-stone-500 hover:bg-stone-200 dark:hover:bg-stone-700 shrink-0"
            data-testid={`doc-copy-transcript-${attachment.filename}`}
            aria-label="Copy transcript"
            title="Copy transcript"
          >
            <ClipboardCopy className="h-3.5 w-3.5" />
          </button>
        </>
      )}
      {editing && (
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); commit(); }}
          className="h-6 w-6 inline-flex items-center justify-center rounded-full bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 shrink-0"
          data-testid={`doc-transcript-save-${attachment.filename}`}
          aria-label="Save transcript"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
